<div class="relative" #datepickerRef>
	<!-- Input Field -->
	<div class="relative w-full">
		<div class="relative">
			<!-- Floating label -->
			@if (label && showFloatingLabel) {
				<label
					for="datepicker-input"
					class="floating-label active text-gray-900 dark:text-gray-100"
				>
					{{ label }}
					@if (isRequired) {
						<span class="text-red-500 text-xs">*</span>
					}
				</label>
			}

			<div class="relative flex items-center">
				<input
					[formControl]="dateControl"
					[readonly]="!allowManualInput"
					(click)="onInputClick($event)"
					(blur)="onInputBlur($event)"
					(keydown.tab)="onTabKey($event)"
					(input)="onInputChange($event)"
					appDateMask
					[dateFormat]="dateFormat"
					[enableMask]="enableDateMask && allowManualInput"
					id="datepicker-input"
					name="datepicker"
					type="text"
					[class]="
						'bg-gray-50 border border-gray-300 text-sm rounded-lg focus:ring-gray-700 focus:border-gray-700 block w-full p-2 pe-8 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-gray-500 dark:focus:border-gray-500 placeholder-gray-500 dark:placeholder-gray-500 ' +
						(allowManualInput
							? ' cursor-text'
							: ' cursor-pointer') +
						' ' +
						inputClass
					"
					[placeholder]="dateFormat"
					[ngClass]="{
						'text-red-600 dark:text-red-500 border-red-300 dark:border-red-500':
							isError || validationError(),
						'text-gray-900 dark:text-white border-gray-300 dark:border-gray-600':
							!isError && !validationError(),
						'!cursor-not-allowed': disabled
					}"
				/>

				<!-- Calendar icon - always on right side -->
				<button
					type="button"
					(click)="toggleCalendar($event)"
					(focus)="onIconFocus($event)"
					(blur)="onIconBlur($event)"
					[disabled]="disabled"
					class="absolute end-2 flex items-center cursor-pointer hover:text-blue-600 focus:text-blue-600 dark:hover:text-blue-400 dark:focus:text-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
				>
					<svg
						[ngClass]="{
							'text-blue-600 dark:text-blue-400': isIconFocused(),
							'text-gray-500 dark:text-gray-400': !isIconFocused()
						}"
						class="w-4 h-4 transition-colors"
						aria-hidden="true"
						xmlns="http://www.w3.org/2000/svg"
						fill="currentColor"
						viewBox="0 0 20 20"
					>
						<path
							d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z"
						/>
					</svg>
				</button>

				<!-- Clear button -->
				@if (!disabled && dateControl.value) {
					<div class="absolute end-8 flex items-center">
						<button
							type="button"
							class="text-gray-400 hover:text-red-500 dark:hover:text-red-300 z-10"
							(click)="clearDate($event)"
							tabindex="-1"
						>
							<svg
								class="size-4"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
				}
			</div>

			<!-- Validation error message -->
			@if (validationError()) {
				<p class="mt-1 text-xs text-red-500 dark:text-red-400">
					{{ validationError() }}
				</p>
			}
		</div>

		<!-- Calendar Template for CDK Overlay Portal -->
		<ng-template #calendarTemplate>
			<div
				#calendarContainer
				class="w-[280px] rounded-xl border border-gray-200 bg-white pt-1.5 shadow-lg dark:border-gray-700 dark:bg-gray-800"
				style="z-index: 99999"
			>
				<!-- Header with Month/Year Selection -->
				<div class="flex items-center justify-between px-3">
					<button
						(click)="prevMonth()"
						[disabled]="isPrevDisabled()"
						class="rounded-md p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke-width="1.5"
							stroke="currentColor"
							class="size-5 text-gray-700 dark:text-gray-300"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
							/>
						</svg>
					</button>

					<div class="flex space-x-2 items-center">
						<button
							(click)="toggleMonthSelect($event)"
							class="px-2 py-1 text-md font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 rounded"
						>
							{{ months[currentDate().getMonth()] }}
						</button>
						<button
							(click)="toggleYearSelect($event)"
							class="px-2 py-1 text-md font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 rounded"
						>
							{{ currentDate().getFullYear() }}
						</button>
					</div>

					<button
						(click)="nextMonth()"
						[disabled]="isNextDisabled()"
						class="rounded-md p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke-width="1.5"
							stroke="currentColor"
							class="size-5 text-gray-700 dark:text-gray-300"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
							/>
						</svg>
					</button>
				</div>

				<!-- Month Select Dropdown -->
				@if (showMonthSelect()) {
					<div
						(click)="$event.stopPropagation()"
						(keydown.enter)="$event.stopPropagation()"
						(keydown.space)="$event.stopPropagation()"
						class="absolute left-1/2 top-16 z-10 -translate-x-1/2 transform rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800 w-[90%] sm:w-[240px]"
					>
						<!-- Jump to current month button -->
						<button
							(click)="jumpToCurrentMonth($event)"
							class="w-full mb-2 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-600 rounded hover:bg-blue-50 dark:text-blue-400 dark:border-blue-500 dark:hover:bg-blue-900/30"
						>
							Current Month
						</button>

						<div class="grid grid-cols-3 gap-2">
							@for (month of abbreviatedMonths(); track $index) {
								<button
									(click)="selectMonth($event, $index)"
									[class.bg-blue-100]="
										$index === currentDate().getMonth()
									"
									[class.dark:bg-blue-900/50]="
										$index === currentDate().getMonth()
									"
									class="px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 rounded"
								>
									{{ month }}
								</button>
							}
						</div>
					</div>
				}

				<!-- Year Select Dropdown with Decade Navigation -->
				@if (showYearSelect()) {
					<div
						(click)="$event.stopPropagation()"
						(keydown.enter)="$event.stopPropagation()"
						(keydown.space)="$event.stopPropagation()"
						class="absolute left-1/2 top-16 z-10 -translate-x-1/2 transform rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800 w-[90%] sm:w-[240px]"
					>
						<!-- Decade navigation -->
						<div class="flex justify-between items-center mb-3">
							<button
								(click)="prevDecade($event)"
								class="px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 rounded"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke-width="2"
									stroke="currentColor"
									class="size-4"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M15.75 19.5 8.25 12l7.5-7.5"
									/>
								</svg>
							</button>

							<button
								(click)="jumpToCurrentYear($event)"
								class="px-3 py-1 text-xs font-medium text-blue-600 border border-blue-600 rounded hover:bg-blue-50 dark:text-blue-400 dark:border-blue-500 dark:hover:bg-blue-900/30"
							>
								Current Year
							</button>

							<button
								(click)="nextDecade($event)"
								class="px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 rounded"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke-width="2"
									stroke="currentColor"
									class="size-4"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="m8.25 4.5 7.5 7.5-7.5 7.5"
									/>
								</svg>
							</button>
						</div>

						<!-- Year grid -->
						<div class="grid grid-cols-3 gap-2">
							@for (year of yearDecade(); track year) {
								<button
									(click)="selectYear($event, year)"
									[class.bg-blue-100]="
										year === currentDate().getFullYear()
									"
									[class.dark:bg-blue-900/50]="
										year === currentDate().getFullYear()
									"
									class="px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 rounded"
								>
									{{ year }}
								</button>
							}
						</div>
					</div>
				}

				<!-- Week days header -->
				<div class="mb-2 mt-3 grid grid-cols-7 px-3">
					@for (day of weekDays; track day) {
						<div
							class="text-center text-xs font-medium text-gray-500 dark:text-gray-400"
						>
							{{ day }}
						</div>
					}
				</div>

				<!-- Calendar days grid -->
				<div class="mt-1 grid grid-cols-7 gap-y-0.5 px-3 pb-2">
					@for (day of calendarDays(); track day) {
						<div
							[ngClass]="getDayClass(day)"
							(click)="!isDateDisabled(day) && selectDate(day)"
							(keydown.enter)="
								!isDateDisabled(day) && selectDate(day)
							"
							(keydown.space)="
								!isDateDisabled(day) && selectDate(day)
							"
						>
							{{ day.getDate() }}
						</div>
					}
				</div>
			</div>
		</ng-template>
	</div>
</div>
